import { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { Check, Zap, Film, Crown, Building2, Instagram, ChevronDown, Lock } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

// ─── Data ──────────────────────────────────────────────────────────────────


// ─── Scroll reveal hook ────────────────────────────────────────────────────

function useScrollReveal() {
  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            entry.target.classList.add('lp-revealed');
            observer.unobserve(entry.target);
          }
        });
      },
      { threshold: 0.08, rootMargin: '0px 0px -48px 0px' }
    );
    document.querySelectorAll('[data-lp-reveal]').forEach((el) => observer.observe(el));
    return () => observer.disconnect();
  }, []);
}

// ─── Component ────────────────────────────────────────────────────────────

export default function Landing() {
  const navigate = useNavigate();
  const [waitlistEmail, setWaitlistEmail] = useState('');
  const [waitlistStatus, setWaitlistStatus] = useState(null); // null | 'loading' | 'success' | 'error'
  const featuresRef = useRef(null);
  useScrollReveal();

  const handleWaitlistSubmit = async (e) => {
    e.preventDefault();
    if (!waitlistEmail.trim()) return;
    setWaitlistStatus('loading');
    try {
      const res = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/waitlist`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email: waitlistEmail.trim() }),
      });
      if (!res.ok) throw new Error('Failed');
      setWaitlistStatus('success');
    } catch {
      setWaitlistStatus('error');
    }
  };

  const handleLogin = () => navigate('/login');
  // Every "join" CTA opens the auth card already in SIGN-UP mode. Sending a
  // first-time visitor to the login card was the biggest drop-off in the
  // onboarding walkthrough: they typed credentials they had never set and
  // were told "Invalid email or password".
  const handleSignup = () => navigate('/signup');

  const scrollToFeatures = () => {
    featuresRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  return (
    <>
      {/* ─── Injected styles ─────────────────────────────────────────────── */}
      <style>{`
        /* Override App.css mobile h1/h2 !important rules inside landing */
        .pangaji-landing h1 { font-size: clamp(3rem, 11vw, 7rem) !important; }
        .pangaji-landing h2 { font-size: inherit !important; }

        /* Film grain via embedded SVG filter */
        .lp-grain {
          position: absolute; inset: 0; pointer-events: none; z-index: 2;
          opacity: 0.5; mix-blend-mode: overlay;
          background-image: url("data:image/svg+xml,%3Csvg viewBox='0 0 256 256' xmlns='http://www.w3.org/2000/svg'%3E%3Cfilter id='g'%3E%3CfeTurbulence type='fractalNoise' baseFrequency='0.85' numOctaves='4' stitchTiles='stitch'/%3E%3CfeColorMatrix type='saturate' values='0'/%3E%3C/filter%3E%3Crect width='100%25' height='100%25' filter='url(%23g)'/%3E%3C/svg%3E");
        }
        /* Scanlines */
        .lp-scanlines {
          position: absolute; inset: 0; pointer-events: none; z-index: 3;
          background: repeating-linear-gradient(0deg, transparent, transparent 3px, rgba(0,0,0,0.07) 3px, rgba(0,0,0,0.07) 4px);
        }
        /* Vignette */
        .lp-vignette {
          position: absolute; inset: 0; pointer-events: none; z-index: 4;
          background: radial-gradient(ellipse at center, transparent 40%, rgba(0,0,0,0.72) 100%);
        }

        /* Scroll reveal */
        [data-lp-reveal] {
          opacity: 0;
          transform: translateY(28px);
          transition: opacity 0.65s ease, transform 0.65s ease;
        }
        [data-lp-reveal].lp-revealed { opacity: 1; transform: translateY(0); }
        [data-lp-delay="1"] { transition-delay: 0.1s; }
        [data-lp-delay="2"] { transition-delay: 0.2s; }
        [data-lp-delay="3"] { transition-delay: 0.3s; }
        [data-lp-delay="4"] { transition-delay: 0.4s; }
        [data-lp-delay="5"] { transition-delay: 0.5s; }
        [data-lp-delay="6"] { transition-delay: 0.6s; }

        /* Amber CTA buttons */
        .lp-btn-primary {
          display: inline-block;
          background: #E8A020; color: #000;
          font-family: 'Courier Prime', monospace; font-weight: 700;
          text-transform: uppercase; letter-spacing: 0.16em; font-size: 0.72rem;
          padding: 15px 34px; border: none; cursor: pointer;
          box-shadow: 0 0 28px rgba(232,160,32,0.35);
          transition: background 0.18s, box-shadow 0.18s;
          white-space: nowrap;
        }
        .lp-btn-primary:hover { background: #f0b030; box-shadow: 0 0 44px rgba(232,160,32,0.6); }

        .lp-btn-ghost {
          display: inline-flex; align-items: center; gap: 8px;
          background: transparent; color: #c8c8c8;
          font-family: 'Courier Prime', monospace; font-weight: 400;
          text-transform: uppercase; letter-spacing: 0.16em; font-size: 0.72rem;
          padding: 14px 34px; border: 1px solid rgba(229,229,229,0.25); cursor: pointer;
          transition: border-color 0.18s, color 0.18s;
          white-space: nowrap;
        }
        .lp-btn-ghost:hover { border-color: rgba(232,160,32,0.55); color: #E8A020; }

        /* Billing toggle */
        .lp-billing-toggle {
          display: inline-flex;
          border: 1px solid rgba(232,160,32,0.3);
          overflow: hidden;
        }
        .lp-billing-toggle button {
          padding: 9px 22px;
          font-family: 'Courier Prime', monospace; font-size: 0.68rem;
          text-transform: uppercase; letter-spacing: 0.1em;
          background: transparent; color: #a1a1aa; border: none; cursor: pointer;
          transition: background 0.18s, color 0.18s;
        }
        .lp-billing-toggle button.active { background: #E8A020; color: #000; font-weight: 700; }

        /* Feature grid hover */
        .lp-feature-cell { transition: background 0.2s; }
        .lp-feature-cell:hover { background: rgba(232,160,32,0.05) !important; }

        /* Pricing card */
        .lp-price-card { transition: transform 0.2s; }
        .lp-price-card:hover { transform: translateY(-4px); }

        /* Beat card */
        .lp-beat-card {
          border-left: 2px solid #E8A020;
          padding: 20px 24px;
          background: rgba(232,160,32,0.03);
          transition: background 0.2s;
          margin-bottom: 2px;
        }
        .lp-beat-card:hover { background: rgba(232,160,32,0.08); }

        /* Africa decorative shape */
        .lp-africa {
          width: 180px; height: 230px;
          background: linear-gradient(145deg, rgba(232,160,32,0.1), rgba(232,160,32,0.03));
          border: 1px solid rgba(232,160,32,0.18);
          border-radius: 48% 44% 55% 42% / 42% 48% 60% 48%;
          position: relative; flex-shrink: 0;
        }
        .lp-africa::after {
          content: '';
          position: absolute; bottom: -28px; left: 52%; transform: translateX(-50%);
          width: 56px; height: 56px;
          background: linear-gradient(145deg, rgba(232,160,32,0.1), rgba(232,160,32,0.03));
          border: 1px solid rgba(232,160,32,0.14);
          border-radius: 42% 58% 52% 48%;
        }

        /* Nav link hover */
        .lp-nav-link { transition: color 0.18s; }
        .lp-nav-link:hover { color: #E8A020 !important; }

        /* Footer link hover */
        .lp-footer-link { transition: color 0.18s; cursor: pointer; }
        .lp-footer-link:hover { color: #E8A020 !important; }

        /* Social icon hover */
        .lp-social { transition: border-color 0.18s; }
        .lp-social:hover { border-color: rgba(232,160,32,0.6) !important; }

        /* Pill tag */
        .lp-pill {
          font-family: 'Courier Prime', monospace; font-size: 0.68rem;
          text-transform: uppercase; letter-spacing: 0.1em;
          color: #E8A020; border: 1px solid rgba(232,160,32,0.3);
          padding: 7px 14px;
        }

        /* Chevron pulse */
        @keyframes lp-bounce { 0%, 100% { transform: translateY(0); } 50% { transform: translateY(6px); } }
        .lp-bounce { animation: lp-bounce 2s ease-in-out infinite; }

        /* Mobile */
        @media (max-width: 640px) {
          .lp-hero-ctas { flex-direction: column !important; align-items: stretch !important; }
          .lp-hero-ctas .lp-btn-primary,
          .lp-hero-ctas .lp-btn-ghost { width: 100%; justify-content: center; text-align: center; }
          .pangaji-landing h1 { font-size: clamp(2.8rem, 14vw, 5rem) !important; }
        }
      `}</style>

      {/* ─── Page wrapper ─────────────────────────────────────────────────── */}
      <div className="pangaji-landing" style={{ background: '#0A0A0A', color: '#e5e5e5', minHeight: '100vh', overflowX: 'hidden' }}>

        {/* ── Sticky nav ──────────────────────────────────────────────────── */}
        <nav style={{
          position: 'fixed', top: 0, left: 0, right: 0, zIndex: 100,
          background: 'rgba(10,10,10,0.88)', backdropFilter: 'blur(14px)',
          borderBottom: '1px solid rgba(232,160,32,0.12)',
          padding: '0 clamp(16px, 5vw, 48px)',
          display: 'flex', alignItems: 'center', justifyContent: 'space-between',
          height: '60px',
        }}>
          <span style={{ fontFamily: 'Courier Prime, monospace', fontWeight: 700, fontSize: '1.05rem', color: '#E8A020', letterSpacing: '0.12em' }}>
            PANGAJI
          </span>
          <div style={{ display: 'flex', gap: '28px', alignItems: 'center' }}>
            <a href="#features" className="lp-nav-link" style={{ fontFamily: 'Courier Prime, monospace', fontSize: '0.68rem', textTransform: 'uppercase', letterSpacing: '0.14em', color: '#a1a1aa', textDecoration: 'none' }}>
              Features
            </a>
            <a href="#pricing" className="lp-nav-link" style={{ fontFamily: 'Courier Prime, monospace', fontSize: '0.68rem', textTransform: 'uppercase', letterSpacing: '0.14em', color: '#a1a1aa', textDecoration: 'none' }}>
              Pricing
            </a>
            <button onClick={handleSignup} className="lp-btn-ghost" style={{ padding: '8px 18px', fontSize: '0.65rem' }}>
              Sign Up
            </button>
            <button onClick={handleLogin} className="lp-btn-primary" style={{ padding: '8px 18px', fontSize: '0.65rem' }}>
              Login
            </button>
          </div>
        </nav>

        {/* ── 1. HERO ─────────────────────────────────────────────────────── */}
        <section style={{
          position: 'relative', minHeight: '100vh',
          display: 'flex', alignItems: 'center', justifyContent: 'center',
          overflow: 'hidden', paddingTop: '60px',
        }}>
          {/* Radial amber glow */}
          <div style={{
            position: 'absolute', inset: 0, zIndex: 1,
            background: 'radial-gradient(ellipse 90% 65% at 50% 42%, rgba(232,160,32,0.08) 0%, transparent 68%), radial-gradient(ellipse 40% 35% at 18% 78%, rgba(232,160,32,0.05) 0%, transparent 55%)',
          }} />
          <div className="lp-grain" />
          <div className="lp-scanlines" />
          <div className="lp-vignette" />

          <div style={{ position: 'relative', zIndex: 10, maxWidth: '860px', margin: '0 auto', padding: '0 clamp(16px, 5vw, 48px)', textAlign: 'center' }}>
            <div style={{ fontFamily: 'Courier Prime, monospace', fontSize: '0.62rem', textTransform: 'uppercase', letterSpacing: '0.4em', color: '#E8A020', marginBottom: '28px', opacity: 0.85 }}>
              ◈ &nbsp;The Story Engine&nbsp; ◈
            </div>

            <h1
              data-testid="hero-title"
              style={{ fontFamily: 'Courier Prime, monospace', fontWeight: 700, lineHeight: 0.92, letterSpacing: '-0.025em', marginBottom: '28px', color: '#ffffff' }}
            >
              PANGAJI
            </h1>

            <p style={{ fontFamily: 'Courier Prime, monospace', fontSize: 'clamp(1.05rem, 2.8vw, 1.45rem)', fontWeight: 700, color: '#ffffff', lineHeight: 1.45, maxWidth: '680px', margin: '0 auto 16px', letterSpacing: '-0.01em' }}>
              Your story deserves a proper engine to drive it to the world.
            </p>

            <p style={{ fontFamily: 'Courier Prime, monospace', fontSize: 'clamp(0.85rem, 2vw, 1.05rem)', color: '#E8A020', lineHeight: 1.6, maxWidth: '580px', margin: '0 auto 28px', letterSpacing: '0.02em', opacity: 0.9 }}>
              Great writers know the story is in the planning. Pangaji is where that planning happens.
            </p>

            <p style={{ fontFamily: 'Courier Prime, monospace', fontSize: 'clamp(0.85rem, 2vw, 1rem)', color: '#b8b8b8', lineHeight: 1.75, maxWidth: '600px', margin: '0 auto 52px', letterSpacing: '0.01em' }}>
              Pangaji is the story development platform built for African filmmakers — from first beat to full bible, with AI that coaches you, never writes for you.
            </p>

            <div className="lp-hero-ctas" style={{ display: 'flex', gap: '16px', justifyContent: 'center', flexWrap: 'wrap' }}>
              <button className="lp-btn-primary" onClick={handleSignup} data-testid="get-started-btn">
                Start Free
              </button>
              <button className="lp-btn-ghost" onClick={scrollToFeatures} data-testid="see-how-btn">
                See How It Works <ChevronDown size={13} />
              </button>
            </div>

            <div className="lp-bounce" style={{ marginTop: '88px', color: 'rgba(232,160,32,0.4)' }}>
              <ChevronDown size={20} style={{ margin: '0 auto' }} />
            </div>
          </div>
        </section>

        {/* ── 2. THE PROBLEM ──────────────────────────────────────────────── */}
        <section style={{ padding: 'clamp(72px, 11vw, 120px) clamp(16px, 5vw, 48px)', background: '#0A0A0A', borderTop: '1px solid rgba(232,160,32,0.1)' }}>
          <div style={{ maxWidth: '860px', margin: '0 auto', textAlign: 'center' }}>
            <div data-lp-reveal>
              <p style={{ fontFamily: 'Courier Prime, monospace', fontSize: '0.62rem', textTransform: 'uppercase', letterSpacing: '0.38em', color: '#E8A020', marginBottom: '20px' }}>The Problem</p>
              <h2 style={{ fontFamily: 'Courier Prime, monospace', fontWeight: 700, fontSize: 'clamp(1.5rem, 4vw, 2.6rem)', letterSpacing: '-0.02em', lineHeight: 1.2, marginBottom: '28px', color: '#fff' }}>
                Generic tools weren't built for your stories.
              </h2>
              <div style={{ width: '56px', height: '2px', background: '#E8A020', margin: '0 auto 36px', opacity: 0.55 }} />
              <p style={{ fontFamily: 'Inter, sans-serif', fontSize: 'clamp(0.9rem, 2vw, 1.05rem)', color: '#909090', lineHeight: 1.88, maxWidth: '700px', margin: '0 auto' }}>
                You're developing a feature set in Nairobi, a series rooted in Kampala, a short film pulling from traditions that no algorithm has ever heard of. You're doing it in scattered notes, disconnected docs, and writing tools built for Hollywood — not for you. Your story is complex. Your development process shouldn't be.
              </p>
            </div>
          </div>
        </section>

        {/* ── 3. FEATURES ─────────────────────────────────────────────────── */}
        <section id="features" ref={featuresRef} style={{ padding: 'clamp(64px, 10vw, 112px) clamp(16px, 5vw, 48px)', background: '#080808' }}>
          <div style={{ maxWidth: '1100px', margin: '0 auto' }}>

            <div data-lp-reveal style={{ textAlign: 'center', marginBottom: '56px' }}>
              <p style={{ fontFamily: 'Courier Prime, monospace', fontSize: '0.62rem', textTransform: 'uppercase', letterSpacing: '0.38em', color: '#E8A020', marginBottom: '16px' }}>Features</p>
              <h2 style={{ fontFamily: 'Courier Prime, monospace', fontWeight: 700, fontSize: 'clamp(1.5rem, 4vw, 2.6rem)', letterSpacing: '-0.02em', marginBottom: '14px' }}>
                Everything your story needs. One place.
              </h2>
              <p style={{ fontFamily: 'Courier Prime, monospace', fontSize: 'clamp(0.78rem, 1.8vw, 0.92rem)', color: '#E8A020', letterSpacing: '0.04em', opacity: 0.85, maxWidth: '520px', margin: '0 auto' }}>
                AI that coaches, never writes. Every word stays yours.
              </p>
            </div>

            {/* 6 feature cards — 2-col grid */}
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(320px, 1fr))', gap: '1px', background: 'rgba(255,255,255,0.055)', marginBottom: '1px' }}>
              {[
                { icon: '◉', title: 'Plot Forge', desc: 'Build your beat sheet act by act. Format-aware structures for short films, features, TV episodes and pilots — with AI feedback on every beat you write.' },
                { icon: '◈', title: 'Character Engine', desc: 'Develop deep, layered characters across 4 levels — identity, psychology, arc, and world. Know your characters before you write them.' },
                { icon: '◇', title: 'Series Builder', desc: 'Develop full TV series — series bible, season arcs, episode beat sheets, pilot structures. From premise to finale, all in one place.' },
                { icon: '⬡', title: 'Character Web', desc: 'Map relationships, conflicts, and power dynamics between every character. See the web, find the gaps.' },
                { icon: '▣', title: 'Scene Builder', desc: 'Build scenes with purpose — action, conflict, turn, polarity shift. Every scene earns its place.' },
                { icon: '◆', title: 'AI Mentor', desc: 'A reflection-agent coach that reads your actual story, critiques its own first draft, and sharpens every response into one specific, actionable question. Never writes — only pushes.' },
                { icon: '▤', title: 'Export Center', desc: 'Export your full story bible as a clean PDF. Choose your sections, preview before download, share with confidence.' },
              ].map((f, i) => (
                <div
                  key={f.title}
                  className="lp-feature-cell"
                  data-lp-reveal
                  data-lp-delay={String((i % 3) + 1)}
                  style={{ background: '#080808', padding: 'clamp(28px, 4vw, 40px) clamp(20px, 3vw, 32px)' }}
                >
                  <span style={{ fontFamily: 'Courier Prime, monospace', fontSize: '1.5rem', color: '#E8A020', display: 'block', marginBottom: '18px' }}>{f.icon}</span>
                  <p style={{ fontFamily: 'Courier Prime, monospace', fontWeight: 700, fontSize: '0.88rem', textTransform: 'uppercase', letterSpacing: '0.08em', marginBottom: '12px', color: '#fff' }}>{f.title}</p>
                  <p style={{ fontFamily: 'Inter, sans-serif', fontSize: '0.875rem', color: '#808080', lineHeight: 1.68 }}>{f.desc}</p>
                </div>
              ))}
            </div>

            {/* Coming soon row */}
            <div data-lp-reveal style={{ marginTop: '28px' }}>
              <p style={{ fontFamily: 'Courier Prime, monospace', fontSize: '0.6rem', textTransform: 'uppercase', letterSpacing: '0.3em', color: '#404040', marginBottom: '14px', textAlign: 'center' }}>
                Coming Soon
              </p>
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))', gap: '1px', background: 'rgba(255,255,255,0.03)' }}>
                {[
                  { title: 'Stage Play Format', desc: 'Beat structures and character development tailored for stage — relevant to East African theatre traditions.' },
                  { title: 'Novel Development', desc: 'Structured development tools for long-form narrative fiction, coming when it\'s done right.' },
                ].map((f) => (
                  <div
                    key={f.title}
                    style={{ background: '#050505', padding: 'clamp(22px, 3vw, 32px) clamp(20px, 3vw, 28px)', opacity: 0.55 }}
                  >
                    <div style={{ display: 'flex', alignItems: 'center', gap: '10px', marginBottom: '14px' }}>
                      <Lock size={14} style={{ color: '#404040', flexShrink: 0 }} />
                      <p style={{ fontFamily: 'Courier Prime, monospace', fontWeight: 700, fontSize: '0.82rem', textTransform: 'uppercase', letterSpacing: '0.08em', color: '#505050', margin: 0 }}>{f.title}</p>
                    </div>
                    <p style={{ fontFamily: 'Inter, sans-serif', fontSize: '0.82rem', color: '#404040', lineHeight: 1.65 }}>{f.desc}</p>
                  </div>
                ))}
              </div>
            </div>

          </div>
        </section>

        {/* ── 4. WAITLIST ─────────────────────────────────────────────────── */}
        <section style={{ padding: 'clamp(64px, 10vw, 100px) clamp(16px, 5vw, 48px)', background: '#060606', borderTop: '1px solid rgba(232,160,32,0.12)' }}>
          <div style={{ maxWidth: '640px', margin: '0 auto', textAlign: 'center' }} data-lp-reveal>
            <p style={{ fontFamily: 'Courier Prime, monospace', fontSize: '0.62rem', textTransform: 'uppercase', letterSpacing: '0.38em', color: '#E8A020', marginBottom: '20px' }}>
              Early Access
            </p>
            <h2 style={{ fontFamily: 'Courier Prime, monospace', fontWeight: 700, fontSize: 'clamp(1.4rem, 4vw, 2.4rem)', letterSpacing: '-0.02em', color: '#fff', marginBottom: '16px', lineHeight: 1.2 }}>
              Be first in. Get early access.
            </h2>
            <p style={{ fontFamily: 'Inter, sans-serif', fontSize: 'clamp(0.88rem, 2vw, 1rem)', color: '#808080', lineHeight: 1.78, marginBottom: '40px' }}>
              Join the waitlist — we'll notify you when new features drop and when spots open up.
            </p>

            {waitlistStatus === 'success' ? (
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '12px', padding: '18px 32px', border: '1px solid rgba(232,160,32,0.4)', background: 'rgba(232,160,32,0.06)' }}>
                <span style={{ color: '#E8A020', fontFamily: 'Courier Prime, monospace', fontSize: '1rem' }}>✓</span>
                <span style={{ fontFamily: 'Courier Prime, monospace', fontSize: '0.88rem', color: '#E8A020', letterSpacing: '0.06em' }}>You're on the list.</span>
              </div>
            ) : (
              <form onSubmit={handleWaitlistSubmit} style={{ display: 'flex', gap: '0', maxWidth: '480px', margin: '0 auto' }}>
                <input
                  type="email"
                  value={waitlistEmail}
                  onChange={(e) => setWaitlistEmail(e.target.value)}
                  placeholder="your@email.com"
                  required
                  style={{
                    flex: 1,
                    background: '#0e0e0e',
                    border: '1px solid rgba(232,160,32,0.3)',
                    borderRight: 'none',
                    color: '#fff',
                    fontFamily: 'Courier Prime, monospace',
                    fontSize: '0.88rem',
                    padding: '14px 18px',
                    outline: 'none',
                    minWidth: 0,
                  }}
                  onFocus={(e) => { e.target.style.borderColor = 'rgba(232,160,32,0.7)'; }}
                  onBlur={(e) => { e.target.style.borderColor = 'rgba(232,160,32,0.3)'; }}
                />
                <button
                  type="submit"
                  disabled={waitlistStatus === 'loading'}
                  style={{
                    background: '#E8A020',
                    color: '#000',
                    border: 'none',
                    fontFamily: 'Courier Prime, monospace',
                    fontWeight: 700,
                    fontSize: '0.72rem',
                    textTransform: 'uppercase',
                    letterSpacing: '0.14em',
                    padding: '14px 24px',
                    cursor: waitlistStatus === 'loading' ? 'not-allowed' : 'pointer',
                    opacity: waitlistStatus === 'loading' ? 0.7 : 1,
                    whiteSpace: 'nowrap',
                    flexShrink: 0,
                  }}
                >
                  {waitlistStatus === 'loading' ? '...' : 'Join Waitlist'}
                </button>
              </form>
            )}

            {waitlistStatus === 'error' && (
              <p style={{ fontFamily: 'Courier Prime, monospace', fontSize: '0.78rem', color: '#c0392b', marginTop: '12px', letterSpacing: '0.04em' }}>
                Something went wrong. Try again.
              </p>
            )}
          </div>
        </section>

        {/* ── 5. CREDIBILITY ──────────────────────────────────────────────── */}
        <section style={{ padding: 'clamp(64px, 10vw, 112px) clamp(16px, 5vw, 48px)', background: '#0A0A0A', borderTop: '1px solid rgba(232,160,32,0.1)', borderBottom: '1px solid rgba(232,160,32,0.1)' }}>
          <div style={{ maxWidth: '860px', margin: '0 auto', textAlign: 'center' }}>
            <div data-lp-reveal>
              <p style={{ fontFamily: 'Courier Prime, monospace', fontSize: '0.62rem', textTransform: 'uppercase', letterSpacing: '0.38em', color: '#E8A020', marginBottom: '20px' }}>Origin Story</p>
              <h2 style={{ fontFamily: 'Courier Prime, monospace', fontWeight: 700, fontSize: 'clamp(1.5rem, 4vw, 2.6rem)', letterSpacing: '-0.02em', lineHeight: 1.2, marginBottom: '28px', color: '#fff' }}>
                Built by a filmmaker.<br /><span style={{ color: '#E8A020' }}>For filmmakers.</span>
              </h2>
              <div style={{ width: '56px', height: '2px', background: '#E8A020', margin: '0 auto 36px', opacity: 0.55 }} />
              <p style={{ fontFamily: 'Inter, sans-serif', fontSize: 'clamp(0.9rem, 2vw, 1.02rem)', color: '#909090', lineHeight: 1.88, maxWidth: '680px', margin: '0 auto' }}>
                Pangaji was built by the team at LAJ House — Nairobi-based filmmakers with Kalasha film and TV market winning stories, international production credits, and a stubborn belief that African storytelling deserves world class recognition, by being told through world class tools.
              </p>
            </div>
          </div>
        </section>

        {/* ── 5. PRICING TEASER ───────────────────────────────────────────── */}
        <section id="pricing" style={{ padding: 'clamp(64px, 10vw, 112px) clamp(16px, 5vw, 48px)', background: '#080808' }}>
          <div style={{ maxWidth: '1100px', margin: '0 auto' }}>

            <div data-lp-reveal style={{ textAlign: 'center', marginBottom: '56px' }}>
              <p style={{ fontFamily: 'Courier Prime, monospace', fontSize: '0.62rem', textTransform: 'uppercase', letterSpacing: '0.38em', color: '#E8A020', marginBottom: '16px' }}>Pricing</p>
              <h2 style={{ fontFamily: 'Courier Prime, monospace', fontWeight: 700, fontSize: 'clamp(1.5rem, 4vw, 2.6rem)', letterSpacing: '-0.02em', marginBottom: '20px' }}>
                Start free. Scale when you're ready.
              </h2>
              <p style={{ fontFamily: 'Inter, sans-serif', fontSize: 'clamp(0.88rem, 2vw, 1rem)', color: '#909090', lineHeight: 1.78, maxWidth: '560px', margin: '0 auto' }}>
                Spark is free — no credit card, no time limit. Build your first story, feel the engine, then upgrade when your project demands more.
              </p>
            </div>

            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))', gap: '1px', background: 'rgba(255,255,255,0.055)', marginBottom: '40px' }}>
              {[
                { key: 'spark',    Icon: Zap,       label: 'Spark',    price: 'Free',            tagline: 'Your first story. One project, unlimited characters, AI Mentor on your first character.', highlight: false },
                { key: 'indie',    Icon: Film,      label: 'Indie',    price: 'KES 499/mo',      tagline: '3 projects. Script editor. 300 AI generations a month. Character Web, Scene Builder, clean exports.', highlight: true, badge: 'Most Popular' },
                { key: 'studio',   Icon: Building2, label: 'Studio',   price: 'KES 1,299/mo',    tagline: 'Unlimited projects and AI Mentor. Series Builder. Modular exports.', highlight: false },
                { key: 'founding', Icon: Crown,     label: 'Founding', price: 'KES 4,999 once',  tagline: 'Studio for life. One payment, no renewals. Only 250 seats.', highlight: false, badge: 'Launch offer' },
              ].map((tier, i) => (
                <div
                  key={tier.key}
                  className="lp-price-card"
                  data-lp-reveal
                  data-lp-delay={String(i + 1)}
                  style={{
                    background: tier.highlight ? 'rgba(232,160,32,0.05)' : '#080808',
                    border: tier.highlight ? '1px solid rgba(232,160,32,0.5)' : '1px solid transparent',
                    boxShadow: tier.highlight ? '0 0 36px rgba(232,160,32,0.1)' : 'none',
                    padding: '36px 28px',
                    display: 'flex', flexDirection: 'column',
                    position: 'relative',
                  }}
                >
                  {tier.badge && (
                    <div style={{
                      position: 'absolute', top: '-1px', left: '50%', transform: 'translateX(-50%)',
                      background: '#E8A020', color: '#000',
                      fontFamily: 'Courier Prime, monospace', fontWeight: 700,
                      fontSize: '0.58rem', textTransform: 'uppercase', letterSpacing: '0.16em',
                      padding: '4px 14px', whiteSpace: 'nowrap',
                    }}>
                      {tier.badge}
                    </div>
                  )}
                  <div style={{ display: 'flex', alignItems: 'center', gap: '10px', marginBottom: '16px', marginTop: tier.badge ? '14px' : 0 }}>
                    <tier.Icon size={17} style={{ color: tier.highlight ? '#E8A020' : '#505050' }} />
                    <span style={{ fontFamily: 'Courier Prime, monospace', fontWeight: 700, fontSize: '0.88rem', textTransform: 'uppercase', letterSpacing: '0.1em', color: tier.highlight ? '#E8A020' : '#e5e5e5' }}>{tier.label}</span>
                  </div>
                  <div style={{ fontFamily: 'Courier Prime, monospace', fontWeight: 700, fontSize: '1.6rem', color: tier.highlight ? '#E8A020' : '#fff', marginBottom: '8px' }}>
                    {tier.price}
                  </div>
                  <p style={{ fontFamily: 'Inter, sans-serif', fontSize: '0.8rem', color: '#606060', lineHeight: 1.6, flex: 1 }}>{tier.tagline}</p>
                </div>
              ))}
            </div>

            <div data-lp-reveal style={{ textAlign: 'center' }}>
              <button className="lp-btn-primary" onClick={handleLogin}>
                See Full Pricing
              </button>
            </div>

          </div>
        </section>

        {/* ── 6. FINAL CTA ────────────────────────────────────────────────── */}
        <section style={{ padding: 'clamp(72px, 10vw, 112px) clamp(16px, 5vw, 48px)', background: '#0A0A0A', borderTop: '1px solid rgba(232,160,32,0.1)', textAlign: 'center' }}>
          <div data-lp-reveal style={{ maxWidth: '640px', margin: '0 auto' }}>
            <p style={{ fontFamily: 'Courier Prime, monospace', fontSize: '0.62rem', textTransform: 'uppercase', letterSpacing: '0.38em', color: '#E8A020', marginBottom: '20px' }}>
              ◈ &nbsp;Get Started&nbsp; ◈
            </p>
            <h2 style={{ fontFamily: 'Courier Prime, monospace', fontWeight: 700, fontSize: 'clamp(1.6rem, 5vw, 3rem)', letterSpacing: '-0.025em', lineHeight: 1.1, marginBottom: '20px', color: '#fff' }}>
              Your next story starts here.
            </h2>
            <p style={{ fontFamily: 'Inter, sans-serif', fontSize: 'clamp(0.9rem, 2vw, 1rem)', color: '#909090', marginBottom: '40px', lineHeight: 1.75, maxWidth: '480px', margin: '0 auto 40px' }}>
              Join independent African filmmakers building stronger stories with Pangaji.
            </p>
            <button className="lp-btn-primary" onClick={handleSignup} data-testid="cta-get-started-btn" style={{ fontSize: '0.75rem', padding: '18px 48px' }}>
              Build your story free
            </button>
            <p style={{ fontFamily: 'Courier Prime, monospace', fontSize: '0.62rem', color: '#404040', marginTop: '16px', letterSpacing: '0.1em' }}>
              No credit card required
            </p>
          </div>
        </section>

        {/* ── 7. FOOTER ───────────────────────────────────────────────────── */}
        <footer style={{ background: '#050505', borderTop: '1px solid rgba(232,160,32,0.1)', padding: 'clamp(48px, 8vw, 72px) clamp(16px, 5vw, 48px) 36px' }}>
          <div style={{ maxWidth: '1100px', margin: '0 auto' }}>

            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(190px, 1fr))', gap: '40px', marginBottom: '48px' }}>

              {/* Brand */}
              <div>
                <div style={{ fontFamily: 'Courier Prime, monospace', fontWeight: 700, fontSize: '1.25rem', color: '#E8A020', letterSpacing: '0.12em', marginBottom: '14px' }}>PANGAJI</div>
                <p style={{ fontFamily: 'Inter, sans-serif', fontSize: '0.8rem', color: '#606060', lineHeight: 1.65, maxWidth: '200px' }}>
                  The story engine for the next generation of African filmmakers.
                </p>
              </div>

              {/* Product */}
              <div>
                <p style={{ fontFamily: 'Courier Prime, monospace', fontSize: '0.62rem', textTransform: 'uppercase', letterSpacing: '0.2em', color: '#e5e5e5', marginBottom: '18px' }}>Product</p>
                {['Features', 'Pricing', 'Character Lab', 'Plot Forge', 'World Building'].map((link) => (
                  <div key={link} style={{ marginBottom: '11px' }}>
                    <a
                      href={link === 'Features' ? '#features' : link === 'Pricing' ? '#pricing' : '#features'}
                      className="lp-footer-link"
                      style={{ fontFamily: 'Inter, sans-serif', fontSize: '0.82rem', color: '#606060', textDecoration: 'none' }}
                    >
                      {link}
                    </a>
                  </div>
                ))}
              </div>

              {/* Account */}
              <div>
                <p style={{ fontFamily: 'Courier Prime, monospace', fontSize: '0.62rem', textTransform: 'uppercase', letterSpacing: '0.2em', color: '#e5e5e5', marginBottom: '18px' }}>Account</p>
                {[['Login','/login'], ['Sign Up','/signup'], ['Dashboard','/dashboard']].map(([link, to]) => (
                  <div key={link} style={{ marginBottom: '11px' }}>
                    <button
                      onClick={() => navigate(to)}
                      className="lp-footer-link"
                      style={{ fontFamily: 'Inter, sans-serif', fontSize: '0.82rem', color: '#606060', background: 'none', border: 'none', padding: 0 }}
                    >
                      {link}
                    </button>
                  </div>
                ))}
              </div>

              {/* Social */}
              <div>
                <p style={{ fontFamily: 'Courier Prime, monospace', fontSize: '0.62rem', textTransform: 'uppercase', letterSpacing: '0.2em', color: '#e5e5e5', marginBottom: '18px' }}>Connect</p>
                <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                  <a
                    href="https://www.instagram.com/usepangaji/"
                    target="_blank"
                    rel="noopener noreferrer"
                    aria-label="PangaJI on Instagram"
                    className="lp-social"
                    style={{ width: '36px', height: '36px', border: '1px solid rgba(232,160,32,0.18)', display: 'flex', alignItems: 'center', justifyContent: 'center', cursor: 'pointer' }}
                  >
                    <Instagram size={14} style={{ color: '#606060' }} />
                  </a>
                  <a
                    href="https://www.instagram.com/usepangaji/"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="lp-footer-link"
                    style={{ fontFamily: 'Inter, sans-serif', fontSize: '0.82rem', color: '#606060', textDecoration: 'none' }}
                  >
                    @usepangaji
                  </a>
                </div>
              </div>

            </div>

            <div style={{ height: '1px', background: 'rgba(232,160,32,0.15)', marginBottom: '24px' }} />

            <div style={{ display: 'flex', justifyContent: 'space-between', flexWrap: 'wrap', gap: '12px' }}>
              <p style={{ fontFamily: 'Courier Prime, monospace', fontSize: '0.68rem', color: '#404040', letterSpacing: '0.05em' }}>
                © 2026 Pangaji — a{' '}
                <a
                  href="https://lajhouse.com"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="lp-footer-link"
                  style={{ color: '#606060', textDecoration: 'none' }}
                >
                  LAJ House Ltd
                </a>{' '}
                company. All rights reserved.
              </p>
              <div style={{ display: 'flex', gap: '20px', alignItems: 'center', flexWrap: 'wrap' }}>
                <a
                  href="/terms"
                  className="lp-footer-link"
                  style={{ fontFamily: 'Courier Prime, monospace', fontSize: '0.68rem', color: '#404040', textDecoration: 'none', letterSpacing: '0.05em' }}
                >
                  Terms of Service
                </a>
                <a
                  href="/privacy"
                  className="lp-footer-link"
                  style={{ fontFamily: 'Courier Prime, monospace', fontSize: '0.68rem', color: '#404040', textDecoration: 'none', letterSpacing: '0.05em' }}
                >
                  Privacy Policy
                </a>
                <p style={{ fontFamily: 'Courier Prime, monospace', fontSize: '0.68rem', color: '#404040', letterSpacing: '0.05em', margin: 0 }}>
                  Made in Nairobi 🇰🇪
                </p>
              </div>
            </div>

          </div>
        </footer>

      </div>
    </>
  );
}
